import * as actionTypes from 'constants/app-action-types';
import * as Immutable from 'immutable';
import {createReducer} from 'redux-store';

import type {List, Map, MapOf} from 'immutable';
import type {ActionsMap} from 'redux-store';

export type AppStore = {
    evaluationTimeRefToIdMap: Map<string, string>;
    evaluationTimeIdToRefMap: Map<string, string>;
    lookupMaps: Map<
        string,
        MapOf<{
            idToRefMap: Map<string, string>;
            refToIdMap: Map<string, string>;
        }>
    >;
};

const initialState = (): AppStore => {
    return {
        evaluationTimeRefToIdMap: Immutable.Map<string, string>(),
        evaluationTimeIdToRefMap: Immutable.Map<string, string>(),
        lookupMaps: Immutable.Map<
            string,
            MapOf<{
                idToRefMap: Map<string, string>;
                refToIdMap: Map<string, string>;
            }>
        >(),
    };
};

/**
 * The app store maintains global state for the application. This is any state
 * that might exist regardless of whether a user is signed in or not, above the
 * level of individual routes/views.
 */
const actionsMap: ActionsMap<AppStore> = {
    [actionTypes.GET_LOOKUP_EVALUATION_TIME_SUCCESS]: (state, action) => {
        const evaluationTimeIdToRefMap: Record<string, string> = {};
        const evaluationTimeRefToIdMap: Record<string, string> = {};
        action.data.forEach((evaluationTime) => {
            evaluationTimeRefToIdMap[evaluationTime.get('ref')] = evaluationTime.get('evaluationTimeId');
            evaluationTimeIdToRefMap[evaluationTime.get('evaluationTimeId')] = evaluationTime.get('ref');
        });
        return {
            evaluationTimeRefToIdMap: Immutable.Map<string, string>(evaluationTimeRefToIdMap),
            evaluationTimeIdToRefMap: Immutable.Map<string, string>(evaluationTimeIdToRefMap),
        };
    },
    [actionTypes.SET_COMET_LOOKUPS]: (
        state,
        action: {
            lookupMap: MapOf<{
                doorPosition: List<
                    MapOf<{
                        ref: string;
                        doorPositionId: string;
                    }>
                >;
                roomColor: List<MapOf<{ref: string; roomColorId: string}>>;
                terminalType: List<MapOf<{ref: string; terminalTypeId: string}>>;
            }>;
        },
    ) => {
        const lookupMaps: Record<
            string,
            MapOf<{
                idToRefMap: Map<string, string>;
                refToIdMap: Map<string, string>;
            }>
        > = {};
        action.lookupMap.forEach((lookupList, lookupKey) => {
            const lookupIdToRefMap: Record<string, string> = {};
            const lookupRefToIdMap: Record<string, string> = {};
            lookupList.forEach((lookup) => {
                lookupRefToIdMap[lookup.get('ref')] = lookup.get(`${lookupKey}Id`);
                lookupIdToRefMap[lookup.get(`${lookupKey}Id`)] = lookup.get('ref');
            });
            lookupMaps[lookupKey] = Immutable.Map<{
                idToRefMap: Map<string, string>;
                refToIdMap: Map<string, string>;
            }>({
                idToRefMap: Immutable.Map<string, string>(lookupIdToRefMap),
                refToIdMap: Immutable.Map<string, string>(lookupRefToIdMap),
            });
        });
        return {
            lookupMaps: Immutable.Map<
                string,
                MapOf<{
                    idToRefMap: Map<string, string>;
                    refToIdMap: Map<string, string>;
                }>
            >(lookupMaps),
        };
    },
    [actionTypes.DISPOSE]: () => {
        return initialState();
    },
};

export default createReducer<AppStore>(initialState(), actionsMap);
